import * as React from 'react';
import { connect } from 'react-redux';
import deburr from 'lodash/deburr';
import flatten from 'lodash/flatten';
import isEmpty from 'lodash/isEmpty';
import kebabCase from 'lodash/kebabCase';

import SortableTable from 'components/sortable-table/SortableTable';
import PopoverScrollable from 'components/popover-scrollable/PopoverScrollable';
import Tooltip from 'components/Tooltip';
import AddNew from 'components/buttons/add-new/AddNew';
import Spinner from 'components/spinner/Spinner';
import InfoNote from 'components/info-note/InfoNote';
import SVGDynamicIcon from 'components/svg-dynamic-icon/SVGDynamicIcon';
import { withModal } from 'components/modal/ModalHOC';

import InsightsReportsModalGeneric from 'modules/insights/reports/components/modals/generic/InsightsReportsModalGeneric';
import InsightsReportsQueriesModal from 'modules/insights/reports/components/queries/modals/InsightsReportsQueriesModal';

import { __t, applyTranslations } from 'utils/language';
import { isDefined } from 'utils/helper/common';
import {
  COLLABARY,
  CREATE,
  DELETE,
  DUPLICATE,
  EDIT,
  FIXED_TO_TOP,
  QUERY
} from 'modules/insights/reports/utils/insights-reports';
import {
  CHANNELS,
  KEYWORDS,
  NOT_FOUND,
  NOT_FOUND_COLOR
} from 'modules/insights/reports/utils/queries/insights-reports-queries';
import {
  getColumnChannels,
  getColumnCheckbox,
  getColumnHashtags,
  getColumnIcons,
  getColumnKeyword
} from 'modules/insights/reports/utils/tables/insights-reports-tables';
import {
  ICONS_MAP,
  enrichQueryIcon,
  getIconDelete,
  getIconDuplicate,
  getIconEdit
} from 'modules/insights/reports/utils/icons/insights-reports-icons';

import './insights-reports-queries-collabary-instagram.scss';

applyTranslations(require('locales/common/collabary-common.i18n.json'));
applyTranslations(require('modules/insights/reports/shared/insights-reports-shared.i18n.json'));
applyTranslations(require('modules/insights/reports/components/queries/insights-reports-queries.i18n.json'));

import type { SVGDynamicIconProps, SortableTableColumnType } from 'types';
import type {
  GeneralProps,
  IconEnriched,
  Query,
  QueryChannel,
  QuerySave,
  Report
} from 'modules/insights/reports/types';

type OwnProps = {
  report: Report,
  isActionEvent: boolean,
  onSaveQuery: (data: QuerySave) => void,
  onUpdateQuery: (query: Query) => void,
  onDuplicateQuery: (query: Query) => void,
  onDeleteQuery: (props: GeneralProps) => void
};

type StateProps = {
  queries: Array<Query>,
  isLoading: boolean
};

type DispatchProps = {};

type Props = OwnProps & StateProps & DispatchProps;

const AddNewWithQueriesModal = withModal(AddNew, {
  modalContent: InsightsReportsQueriesModal,
  width: 'small',
  positionOverride: FIXED_TO_TOP
});

const IconWithQueriesModal = withModal(Tooltip, {
  modalContent: InsightsReportsQueriesModal,
  width: 'small',
  positionOverride: FIXED_TO_TOP
});

const IconWithGenericModal = withModal(Tooltip, {
  modalContent: InsightsReportsModalGeneric,
  width: 'small',
  positionOverride: FIXED_TO_TOP
});

const COLLABARY_MSG_KEY = 'collabary_common';
const SHARED_MSG_KEY = 'insights_reports_shared';
const MSG_KEY = 'insights_reports_queries';

class InsightsReportsQueriesCollabaryInstagram extends React.PureComponent<Props> {
  getIconEdit(query: Query): IconEnriched {
    const { report } = this.props;
    const icon = getIconEdit();

    return enrichQueryIcon(query, report, icon);
  }

  getIconDuplicate(query: Query): IconEnriched {
    const { report, onDuplicateQuery } = this.props;
    const icon = getIconDuplicate(onDuplicateQuery);

    return enrichQueryIcon(query, report, icon);
  }

  getIconDelete(query: Query): IconEnriched {
    const { report, onDeleteQuery } = this.props;
    const icon = getIconDelete(onDeleteQuery);

    return enrichQueryIcon(query, report, icon);
  }

  getModalTitle(type: string): string {
    return __t(`${MSG_KEY}.labels.${type}`);
  }

  getModalLabels(type: string): GeneralProps {
    return {
      actionButton: __t(`${MSG_KEY}.buttons.${type}`)
    };
  }

  getColumns(): Array<SortableTableColumnType> {
    const { queries } = this.props;

    if (isEmpty(queries)) {
      return [];
    }

    return [
      getColumnCheckbox(this.handleToggleQuery, 'insights-reports-queries-collabary-instagram__column-status'),
      getColumnKeyword(MSG_KEY, 'insights-reports-queries-collabary-instagram__column-keyword'),
      getColumnHashtags(this.renderColumnTitle, this.renderColumnText),
      getColumnChannels(this.renderColumnTitle, this.renderChannelColumnText),
      getColumnIcons(this.renderIconModal, 'insights-reports-queries-collabary-instagram__column-icons')
    ];
  }

  getQueryData(): Array<Query> {
    const { queries } = this.props;

    return queries.map(query => ({
      ...query,
      icons: [
        this.getIconEdit(query),
        this.getIconDuplicate(query),
        this.getIconDelete(query)
      ]
    }));
  }

  getSortedChannels(channels: Array<QueryChannel>): Array<QueryChannel> {
    const nonErrorItems = channels.filter(item => item.status !== NOT_FOUND);
    const errorItems = channels.filter(item => item.status === NOT_FOUND);

    return errorItems.concat(nonErrorItems);
  }

  getErrorStatus(channels: Array<QueryChannel>): boolean {
    return (channels || []).some(channel => channel.status === NOT_FOUND);
  }

  handleToggleQuery = (
    event: React.SyntheticEvent<HTMLInputElement>,
    query: Query
  ): void => {
    const { onUpdateQuery } = this.props;

    const updatedQuery = {
      ...query,
      enabled: !query.enabled
    };

    onUpdateQuery(updatedQuery);
  };

  handleRowStatus = (query: any): string => (
    this.getErrorStatus(query[CHANNELS]) ? 'error' : ''
  );

  renderColumnTitle = (
    columnName: string
  ): JSX.Element => {
    const isChannelsColumn = columnName === CHANNELS;

    return (
      <div className="insights-reports-queries-collabary-instagram__column-title">
        <div>
          {__t(`${MSG_KEY}.labels.included_${columnName}`)}
        </div>
        {
          !isChannelsColumn && (
            <Tooltip
              message={__t(`${SHARED_MSG_KEY}.tooltips.${columnName}`)}
              width="long"
              textAlign="left"
              classNameWrapper="insights-reports-queries-collabary-instagram__column-title-tooltip"
              showQuestionMark
            />
          )
        }
      </div>
    );
  };

  renderColumnText = (
    query: Query,
    columnType: string
  ): JSX.Element => {
    const hashtags = query[KEYWORDS] || [];

    return (
      <PopoverScrollable
        withFixedVerticalScroll
        identifier={columnType}
        size="small"
        classNameWrapper="insights-reports-queries-collabary-instagram__popover-wrapper text-overflow-ellipsis"
        classNameTrigger="text-overflow-ellipsis"
        disabled={!query.enabled}
        list={hashtags.map(hashtag => ({
          text: hashtag.keyword
        }))}
        trigger={(
          <React.Fragment>
            {hashtags.map(h => `#${h.keyword}`).join(', ')}
          </React.Fragment>
        )}
      />
    );
  };

  renderColumnChannels(query: Query): Array<JSX.Element> {
    const items = query[CHANNELS] || [];
    const updatedItems = this.getSortedChannels(items);

    return updatedItems.filter(isDefined)
      .filter(item => item.username.length > 0)
      .map((item, index) => (
        <React.Fragment key={`insights-reports-queries-collabary-instagram__channel--${kebabCase(deburr(item.username))}`}>
          <span className={`insights-reports-queries-collabary-instagram--status-${kebabCase(item.status)}`}>
            {item.username}
          </span>
          {
            index < items.length - 1 && (
              <span>,&nbsp;</span>
            )
          }
        </React.Fragment>
      ));
  }

  renderChannelColumnText = (
    query: Query
  ): JSX.Element => (
    <PopoverScrollable
      withFixedVerticalScroll
      identifier="channels"
      size="small"
      classNameWrapper="insights-reports-queries-collabary-instagram__popover-wrapper text-overflow-ellipsis"
      classNameTrigger="text-overflow-ellipsis"
      disabled={!query.enabled}
      list={this.getSortedChannels(query[CHANNELS]!).map(channel => ({
        color: channel.status === NOT_FOUND ? NOT_FOUND_COLOR : '',
        text: channel.username,
        title: `${__t(`${COLLABARY_MSG_KEY}.basics.open`)} ${__t(`${COLLABARY_MSG_KEY}.social.instagram`)}`,
        url: `https://www.instagram.com/${channel.username}`
      }))}
      trigger={(
        <React.Fragment>
          {this.renderColumnChannels(query)}
        </React.Fragment>
      )}
    />
  );

  renderIconModal = (
    icon: IconEnriched,
    iconProps: SVGDynamicIconProps
  ): React.ReactNode | undefined | null => {
    if (icon.name === ICONS_MAP.edit) {
      return this.renderModalEdit(icon, iconProps);
    }

    if (icon.name === ICONS_MAP.duplicate) {
      return this.renderModalDuplicate(icon, iconProps);
    }

    if (icon.name === ICONS_MAP.delete) {
      return this.renderModalDelete(icon, iconProps);
    }

    return null;
  };

  renderModalEdit(icon: IconEnriched, iconProps: SVGDynamicIconProps): React.ReactNode {
    const { report, onSaveQuery } = this.props;

    return (
      <IconWithQueriesModal
        message={icon.tooltip}
        width="micro"
        modalContentProps={{
          report,
          query: icon.query,
          pageType: COLLABARY,
          sectionType: QUERY,
          actionType: EDIT,
          stepIndexStart: this.getErrorStatus(icon.query[CHANNELS]!) ? 1 : null,
          onSaveQuery
        }}
      >
        <SVGDynamicIcon {...iconProps} />
      </IconWithQueriesModal>
    );
  }

  renderModalDuplicate(icon: IconEnriched, iconProps: SVGDynamicIconProps): React.ReactNode {
    return (
      <IconWithGenericModal
        message={icon.tooltip}
        width="micro"
        modalContentProps={{
          title: this.getModalTitle(DUPLICATE),
          labels: this.getModalLabels(DUPLICATE),
          message: __t(`${MSG_KEY}.messages.${DUPLICATE}`, {
            queryName: icon.query.name || ''
          }),
          report: icon.report,
          reportType: icon.report.reportType,
          query: icon.query,
          sectionType: QUERY,
          actionType: DUPLICATE,
          onAction: icon.onDuplicate
        }}
      >
        <SVGDynamicIcon {...iconProps} />
      </IconWithGenericModal>
    );
  }

  renderModalDelete(icon: IconEnriched, iconProps: SVGDynamicIconProps): React.ReactNode {
    return (
      <IconWithGenericModal
        message={icon.tooltip}
        width="micro"
        modalContentProps={{
          title: this.getModalTitle(DELETE),
          labels: this.getModalLabels(DELETE),
          message: __t(`${MSG_KEY}.messages.${DELETE}`, {
            queryName: icon.query.name || ''
          }),
          report: icon.report,
          reportType: icon.report.reportType,
          query: icon.query,
          sectionType: QUERY,
          actionType: DELETE,
          onAction: icon.onDelete
        }}
      >
        <SVGDynamicIcon {...iconProps} />
      </IconWithGenericModal>
    );
  }

  renderSpinner(): React.ReactNode {
    return (
      <div className="insights-reports-queries-collabary-instagram__spinner">
        <Spinner
          position="inline"
          size="small"
          color="light"
        />
      </div>
    );
  }

  renderAddNew(): React.ReactNode {
    const { report, onSaveQuery } = this.props;

    return (
      <div className="insights-reports-queries-collabary-instagram__add-new">
        <AddNewWithQueriesModal
          text={__t(`${SHARED_MSG_KEY}.labels.add_new`)}
          tooltip={__t(`${MSG_KEY}.tooltips.add_new`)}
          modalContentProps={{
            report,
            query: {},
            pageType: COLLABARY,
            sectionType: QUERY,
            actionType: CREATE,
            onSaveQuery
          }}
          icon={{
            name: 'plus-inside-circle-custom-bg-white-fg',
            width: 16,
            height: 16
          }}
        />
      </div>
    );
  }

  renderHeader(): React.ReactNode {
    return (
      <div className="insights-reports-queries-collabary-instagram__header">
        <div className="insights-reports-queries-collabary-instagram__header-text">
          {__t(`${MSG_KEY}.labels.queries`)}
        </div>
        {this.renderAddNew()}
      </div>
    );
  }

  renderInfoNote(): React.ReactNode {
    const { queries } = this.props;
    const allChannels = flatten(queries.map(query => query[CHANNELS] || []));
    const hasError = this.getErrorStatus(allChannels);

    if (!hasError) {
      return null;
    }

    return (
      <InfoNote
        type="error"
        size="normal"
        hideIcon
      >
        <div>
          {__t(`${MSG_KEY}.errors.no_data`)}
        </div>
      </InfoNote>
    );
  }

  renderTableOverlay(): React.ReactNode {
    return (
      <div className="insights-reports-queries-collabary-instagram__table-overlay">
        {this.renderSpinner()}
      </div>
    );
  }

  renderTable(): React.ReactNode {
    const { isLoading, isActionEvent } = this.props;
    const queryData = this.getQueryData();

    return (
      <React.Fragment>
        {
          isLoading && isActionEvent &&
            this.renderTableOverlay()
        }
        <SortableTable
          data={queryData}
          columns={this.getColumns()}
          layout="simple"
          maxHeight={286}
          getRowStatus={this.handleRowStatus}
        />
      </React.Fragment>
    );
  }

  render(): React.ReactNode {
    const { isLoading, isActionEvent } = this.props;

    return (
      <div className="insights-reports-queries-collabary-instagram">
        {this.renderInfoNote()}
        <div className="insights-reports-queries-collabary-instagram__list">
          {this.renderHeader()}
          <div className="insights-reports-queries-collabary-instagram__table">
            {
              isLoading && !isActionEvent
                ? this.renderSpinner()
                : this.renderTable()
            }
          </div>
        </div>
      </div>
    );
  }
}

const mapStateToProps = ({ insightsReportsQueries }): StateProps => ({
  queries: insightsReportsQueries.queries,
  isLoading: insightsReportsQueries.isLoading
});

export default connect(mapStateToProps)(InsightsReportsQueriesCollabaryInstagram);
